import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import * as m from '../../localization/out/messages.js';
import { router } from '../startup.js';
import { SVR_ENABLED } from './ticket-detail-page.js';

export const SVRPage = stateful(({ number }: { number: string }) => {
  // SVR is feature-disabled; guard the deep-link (the route stays registered for when it's back).
  // Stable const, so this early return is consistent across renders — no hook-order risk.
  if (!SVR_ENABLED) {
    router.goTo('tickets');
    return html``;
  }

  const [description, setDescription] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  const canGenerate = description.trim().length > 0 && additionalNotes.trim().length > 0;

  return html`
    <div class="px-4 py-5 max-w-5xl mx-auto flex flex-col gap-5">

      <!-- Back + Header -->
      <div>
        <a href="${router.link('ticketDetail', { params: { number } })}" class="btn btn-ghost btn-sm gap-1 -ml-2 mb-2">
          <span class="icon-[material-symbols--arrow-back] text-lg"></span>
          ${m.svr_back()}
        </a>
        <h1 class="text-2xl font-bold">${m.svr_title()}</h1>
        <p class="text-sm text-base-content/60 mt-1">${m.svr_subtitle()}</p>
      </div>

      <!-- Onsite Protocol row -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm p-4 flex flex-col gap-1 h-full">
          <label class="text-xs font-semibold text-base-content/60 uppercase tracking-wide">${m.svr_onsite_protocol()}</label>
          <textarea
            class="textarea textarea-bordered w-full flex-1 min-h-40 text-sm"
            placeholder="${m.svr_onsite_protocol_placeholder()}"
            .value=${description}
            @input=${(e: InputEvent) => setDescription((e.target as HTMLTextAreaElement).value)}
          ></textarea>
        </div>
        <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm p-4 flex flex-col gap-3 h-full">
          <h3 class="font-semibold text-sm flex items-center gap-2">
            <span class="icon-[material-symbols--checklist] text-lg text-primary"></span>
            ${m.svr_guidance_onsite_title()}
          </h3>
          <ul class="text-sm text-base-content/70 flex flex-col gap-2 pl-1">
            <li class="flex gap-2"><span class="text-base-content/30">-</span>${m.svr_guidance_onsite_1()}</li>
            <li class="flex gap-2"><span class="text-base-content/30">-</span>${m.svr_guidance_onsite_2()}</li>
            <li class="flex gap-2"><span class="text-base-content/30">-</span>${m.svr_guidance_onsite_3()}</li>
            <li class="flex gap-2"><span class="text-base-content/30">-</span>${m.svr_guidance_onsite_4()}</li>
            <li class="flex gap-2"><span class="text-base-content/30">-</span>${m.svr_guidance_onsite_5()}</li>
            <li class="flex gap-2"><span class="text-base-content/30">-</span>${m.svr_guidance_onsite_6()}</li>
            <li class="flex gap-2"><span class="text-base-content/30">-</span>${m.svr_guidance_onsite_7()}</li>
          </ul>
        </div>
      </div>

      <!-- Hardware Information row -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm p-4 flex flex-col gap-1 h-full">
          <label class="text-xs font-semibold text-base-content/60 uppercase tracking-wide">${m.svr_hardware_info()}</label>
          <textarea
            class="textarea textarea-bordered w-full flex-1 min-h-28 text-sm"
            placeholder="${m.svr_hardware_info_placeholder()}"
            .value=${additionalNotes}
            @input=${(e: InputEvent) => setAdditionalNotes((e.target as HTMLTextAreaElement).value)}
          ></textarea>
        </div>
        <div class="rounded-2xl border border-base-300 bg-base-100 shadow-sm p-4 flex flex-col gap-3 h-full">
          <h3 class="font-semibold text-sm flex items-center gap-2">
            <span class="icon-[material-symbols--memory] text-lg text-primary"></span>
            ${m.svr_guidance_hardware_title()}
          </h3>
          <ul class="text-sm text-base-content/70 flex flex-col gap-2 pl-1">
            <li class="flex gap-2"><span class="text-base-content/30">-</span>${m.svr_guidance_hardware_1()}</li>
            <li class="flex gap-2"><span class="text-base-content/30">-</span>${m.svr_guidance_hardware_2()}</li>
            <li class="flex gap-2"><span class="text-base-content/30">-</span>${m.svr_guidance_hardware_3()}</li>
            <li class="flex gap-2"><span class="text-base-content/30">-</span>${m.svr_guidance_hardware_4()}</li>
            <li class="flex gap-2"><span class="text-base-content/30">-</span>${m.svr_guidance_hardware_5()}</li>
          </ul>
        </div>
      </div>

      <!-- Generate button -->
      <button
        class="btn btn-primary w-full sm:w-auto sm:self-end gap-1"
        ?disabled=${!canGenerate}
        @click=${() => {
          // SVR generation will be implemented when backend endpoint is available
        }}
      >
        <span class="icon-[material-symbols--description] text-lg"></span>
        ${m.svr_generate()}
      </button>

    </div>
  `;
});
